// Gravity vector per photo, for footscan-server's IBV step.
//
// IBV's reconstruction takes, per photo, the phone's gravity vector next to the
// image (the old iOS app sent CoreMotion `gravity` in g, times -9.8). The SPA
// has no CoreMotion, so we record the browser's
// `DeviceMotionEvent.accelerationIncludingGravity` instead, averaged over the
// last few samples before the shutter so hand shake mostly cancels out, and
// divided by standard gravity to land in g.
//
// Axes: x to the right of the screen, y towards the top of the device, z out
// of the screen. The sign is whatever the browser reports, and it differs: iOS
// Safari reads like CoreMotion's `gravity` (pointing down; the preview scan
// G13EKI, portrait at ~45°, gave about [0, -0.7, -0.7]), while the W3C spec,
// and so presumably Android Chrome, has the opposite sign. The relay owns that
// conversion (`device` in the sidecar names the platform), so the sidecar keeps
// the raw browser reading.

const STANDARD_GRAVITY = 9.80665;

// ~250 ms at the usual 60 Hz devicemotion rate: long enough to smooth a tap on
// the shutter, short enough that the phone has not moved since.
const WINDOW = 15;

/** Rolling window of `accelerationIncludingGravity` samples, in m/s². */
export class GravitySampler {
  private samples: Array<[number, number, number]> = [];

  push(x: number | null | undefined, y: number | null | undefined, z: number | null | undefined): void {
    if (![x, y, z].every((v) => typeof v === "number" && Number.isFinite(v))) return;
    this.samples.push([x as number, y as number, z as number]);
    if (this.samples.length > WINDOW) this.samples.shift();
  }

  /** Mean of the window in g, rounded to 4 decimals; null without samples. */
  meanInG(): [number, number, number] | null {
    const n = this.samples.length;
    if (n === 0) return null;
    const sum = this.samples.reduce(
      (acc, s) => [acc[0] + s[0], acc[1] + s[1], acc[2] + s[2]],
      [0, 0, 0],
    );
    const g = (v: number): number => Math.round((v / n / STANDARD_GRAVITY) * 1e4) / 1e4;
    return [g(sum[0]), g(sum[1]), g(sum[2])];
  }

  clear(): void {
    this.samples = [];
  }
}
